// Confidence Utilities
// Integration layer for the confidence filtering architecture

import {
  PayrollCase,
  getDetailedConfidenceAssessment,
  ConfidenceError,
  ConfidenceErrorState,
  createConfidenceError,
  isErrorRecoverable,
  getErrorDisplayInfo,
  ERROR_CODES,
  DEFAULT_RETRY_STRATEGIES,
} from '../types';
import {
  evaluateConfidence,
  generateCaseFlagging,
  calculateConfidenceMetrics,
} from '../types/confidenceArchitecture';
import { routeCase, generateRoutingSummary } from '../config/confidenceRouting';

// Task #16: Error-aware confidence evaluation with recovery
const evaluateCaseWithErrorHandling = async (
  caseItem: PayrollCase,
  additionalFactors?: {
    processingAttempts?: number;
    dataQualityScore?: number;
    documentComplexity?: number;
  }
): Promise<{
  evaluation: any;
  error?: ConfidenceError;
  recovered: boolean;
}> => {
  try {
    const evaluation = evaluateCase(caseItem, additionalFactors);
    return { evaluation, recovered: false };
  } catch (error: any) {
    const confidenceError = createConfidenceError(
      'confidence_calculation',
      ERROR_CODES.CONFIDENCE_CALC_FAILED,
      `Failed to evaluate case ${caseItem.caseNumber}: ${error.message}`,
      {
        caseNumber: caseItem.caseNumber,
        originalConfidence: caseItem.aiConfidence,
        context: {
          operation: 'evaluate_case',
          parameters: { caseNumber: caseItem.caseNumber, additionalFactors },
          stackTrace: error.stack,
          userAction: 'case_evaluation',
        },
      }
    );

    // Attempt recovery with fallback evaluation
    const fallbackEvaluation = getFallbackEvaluation(caseItem);
    return {
      evaluation: fallbackEvaluation,
      error: confidenceError,
      recovered: true,
    };
  }
};

// Main utility function for comprehensive case evaluation
export const evaluateCase = (
  caseItem: PayrollCase,
  additionalFactors?: {
    processingAttempts?: number;
    dataQualityScore?: number;
    documentComplexity?: number;
  }
) => {
  try {
    // Validate input confidence
    if (
      typeof caseItem.aiConfidence !== 'number' ||
      caseItem.aiConfidence < 0 ||
      caseItem.aiConfidence > 100
    ) {
      throw new Error(`Invalid confidence value: ${caseItem.aiConfidence}`);
    }

    // Get detailed assessment using new architecture
    const fullAssessment = evaluateConfidence(caseItem.aiConfidence, additionalFactors);

    // Get routing information
    const routing = routeCase(caseItem, fullAssessment);

    // Get simple assessment for backward compatibility
    const simpleAssessment = getDetailedConfidenceAssessment(caseItem.aiConfidence);

    // Generate routing summary
    const summary = generateRoutingSummary(caseItem, fullAssessment);

    return {
      // Core assessment data
      confidence: fullAssessment.score,
      originalConfidence: caseItem.aiConfidence,
      level: fullAssessment.level,
      isVisible: fullAssessment.isVisible,

      // Routing information
      workflow: routing.workflow,
      estimatedTime: fullAssessment.estimatedProcessingTime,
      requiresCSR: routing.workflow !== 'AUTO_PROCESS',

      // Quality assurance
      qaCheckpoints: routing.qaCheckpoints,
      additionalValidation: fullAssessment.additionalValidation,

      // Flagging
      requiresFlag: fullAssessment.requiresFlag,
      flagReason: fullAssessment.flagReason,
      flagging: routing.flagging,

      // Notifications
      notifications: routing.notifications,

      // Summary for display
      summary: summary.summary,

      // Backward compatibility
      simple: simpleAssessment,

      // Error state (Task #16)
      hasErrors: false,
      errors: [],
    };
  } catch (error: any) {
    // Return fallback evaluation with error information
    return getFallbackEvaluation(caseItem, error);
  }
};

// Task #16: Fallback evaluation function for error recovery
const getFallbackEvaluation = (caseItem: PayrollCase, error?: any) => {
  // Create a basic evaluation using simple confidence assessment
  const simpleAssessment = getDetailedConfidenceAssessment(caseItem.aiConfidence);

  return {
    // Core assessment data (using simple fallback)
    confidence: caseItem.aiConfidence,
    originalConfidence: caseItem.aiConfidence,
    level: simpleAssessment.detailed,
    isVisible: simpleAssessment.isVisible,

    // Basic routing information
    workflow: simpleAssessment.recommendedWorkflow,
    estimatedTime: 'Unknown',
    requiresCSR: true, // Conservative approach

    // Basic quality assurance
    qaCheckpoints: [],
    additionalValidation: true,

    // Flagging (conservative approach)
    requiresFlag: simpleAssessment.requiresFlag,
    flagReason: error ? `Evaluation error: ${error.message}` : 'Evaluation failed',
    flagging: { required: simpleAssessment.requiresFlag },

    // Notifications
    notifications: [],

    // Summary for display
    summary: `Case ${caseItem.caseNumber} - Fallback evaluation due to processing error`,

    // Backward compatibility
    simple: simpleAssessment,

    // Error state (Task #16)
    hasErrors: true,
    errors: error ? [error] : [],
    isFallback: true,
  };
};

// Task #16: Retry utility with exponential backoff
const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  retryStrategy = DEFAULT_RETRY_STRATEGIES.EXPONENTIAL_BACKOFF,
  maxRetries = 3
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with jitter if enabled
      let delay = retryStrategy.baseDelay * Math.pow(retryStrategy.multiplier, attempt);
      delay = Math.min(delay, retryStrategy.maxDelay);

      if (retryStrategy.jitter) {
        delay = delay * (0.5 + Math.random() * 0.5); // Add 50% jitter
      }

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

// Task #16: Enhanced case flagging with error handling
const flagCaseWithErrorHandling = async (
  caseNumber: string,
  reason: string,
  options: { retryOnFailure?: boolean; maxRetries?: number } = {}
): Promise<{
  success: boolean;
  error?: ConfidenceError;
  flagged: boolean;
}> => {
  const { retryOnFailure = true, maxRetries = 3 } = options;

  const flagOperation = async () => {
    // Mock API call to flag case in Dynamics
    const response = await fetch('/api/dynamics/flag-cases', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        caseIds: [caseNumber],
        reason,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  };

  try {
    if (retryOnFailure) {
      await retryWithBackoff(
        flagOperation,
        DEFAULT_RETRY_STRATEGIES.EXPONENTIAL_BACKOFF,
        maxRetries
      );
    } else {
      await flagOperation();
    }

    return { success: true, flagged: true };
  } catch (error: any) {
    const confidenceError = createConfidenceError(
      'flagging_failed',
      ERROR_CODES.FLAG_DYNAMICS_UNREACHABLE,
      `Failed to flag case ${caseNumber}: ${error.message}`,
      {
        caseNumber,
        context: {
          operation: 'flag_case',
          parameters: { caseNumber, reason },
          stackTrace: error.stack,
          userAction: 'flag_case',
        },
      }
    );

    return { success: false, error: confidenceError, flagged: false };
  }
};

// Task #16: Error state management utilities
const createErrorState = (errors: ConfidenceError[] = []): ConfidenceErrorState => {
  const criticalErrors = errors.filter((e) => e.severity === 'critical');
  const errorsByCase = errors.reduce(
    (acc, error) => {
      if (error.caseNumber) {
        acc[error.caseNumber] = acc[error.caseNumber] || [];
        acc[error.caseNumber].push(error);
      }
      return acc;
    },
    {} as Record<string, ConfidenceError[]>
  );

  return {
    hasErrors: errors.length > 0,
    errors,
    errorsByCase,
    criticalErrors,
    isRecovering: false,
    lastErrorAt: errors.length > 0 ? errors[errors.length - 1].timestamp : undefined,
    errorSummary: {
      totalErrors: errors.length,
      recoverableErrors: errors.filter((e) => isErrorRecoverable(e)).length,
      criticalErrors: criticalErrors.length,
      casesAffected: Object.keys(errorsByCase).length,
    },
  };
};

// Utility for batch evaluation of multiple cases
export const evaluateMultipleCases = (cases: PayrollCase[]) => {
  const evaluations = cases.map((caseItem) => evaluateCase(caseItem));
  const metrics = calculateConfidenceMetrics(cases);

  // Calculate additional batch metrics
  const workflowDistribution = evaluations.reduce(
    (acc, evaluation) => {
      acc[evaluation.workflow] = (acc[evaluation.workflow] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const flaggedCases = evaluations.filter((evaluation) => evaluation.requiresFlag);
  const visibleCases = evaluations.filter((evaluation) => evaluation.isVisible);

  return {
    evaluations,
    metrics,
    summary: {
      totalCases: cases.length,
      visibleCases: visibleCases.length,
      flaggedCases: flaggedCases.length,
      workflowDistribution,
      averageConfidence: metrics.averageConfidence,
      flaggingRate: metrics.flaggedPercentage,
    },
  };
};

// Utility for getting confidence display information
export const getConfidenceDisplayInfo = (confidence: number) => {
  const assessment = getDetailedConfidenceAssessment(confidence);

  // Enhanced display configuration
  const displayConfig = {
    auto_process: {
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200',
      icon: '⚡',
      label: 'Auto-Process',
      description: 'Automated processing',
    },
    high: {
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200',
      icon: '✓',
      label: 'High',
      description: 'Quick review required',
    },
    medium: {
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-200',
      icon: '⚠',
      label: 'Medium',
      description: 'Standard review required',
    },
    low: {
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200',
      icon: '!',
      label: 'Low',
      description: 'Enhanced review required',
    },
    critical: {
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-200',
      icon: '❌',
      label: 'Critical',
      description: 'Manual escalation required',
    },
  };

  const config = displayConfig[assessment.detailed];

  return {
    confidence,
    level: assessment.level,
    detailedLevel: assessment.detailed,
    isVisible: assessment.isVisible,
    requiresFlag: assessment.requiresFlag,
    workflow: assessment.recommendedWorkflow,
    display: {
      ...config,
      percentage: `${confidence}%`,
      progressWidth: `${Math.min(confidence, 100)}%`,
    },
  };
};

// Utility for filtering cases based on confidence criteria
export const filterCasesByConfidence = (
  cases: PayrollCase[],
  criteria: {
    minConfidence?: number;
    maxConfidence?: number;
    levels?: ('auto_process' | 'high' | 'medium' | 'low' | 'critical')[];
    visibleOnly?: boolean;
    flaggedOnly?: boolean;
    workflow?: string;
  }
) => {
  return cases.filter((caseItem) => {
    const evaluation = evaluateCase(caseItem);

    // Min/max confidence checks
    if (
      criteria.minConfidence !== undefined &&
      evaluation.confidence < criteria.minConfidence
    ) {
      return false;
    }
    if (
      criteria.maxConfidence !== undefined &&
      evaluation.confidence > criteria.maxConfidence
    ) {
      return false;
    }

    // Level filter
    if (criteria.levels && criteria.levels.indexOf(evaluation.level) === -1) {
      return false;
    }

    // Visibility filter
    if (criteria.visibleOnly && !evaluation.isVisible) {
      return false;
    }

    // Flagged filter
    if (criteria.flaggedOnly && !evaluation.requiresFlag) {
      return false;
    }

    // Workflow filter
    if (criteria.workflow && evaluation.workflow !== criteria.workflow) {
      return false;
    }

    return true;
  });
};

// Utility for generating confidence reports
export const generateConfidenceReport = (cases: PayrollCase[]) => {
  const batchEvaluation = evaluateMultipleCases(cases);
  const { evaluations, summary } = batchEvaluation;

  // Generate detailed breakdown
  const levelBreakdown = {
    auto_process: evaluations.filter((e) => e.level === 'auto_process').length,
    high: evaluations.filter((e) => e.level === 'high').length,
    medium: evaluations.filter((e) => e.level === 'medium').length,
    low: evaluations.filter((e) => e.level === 'low').length,
    critical: evaluations.filter((e) => e.level === 'critical').length,
  };

  const workflowBreakdown = summary.workflowDistribution;

  const flaggedBreakdown = evaluations
    .filter((e) => e.requiresFlag)
    .reduce(
      (acc, e) => {
        const reason = e.flagReason || 'Unknown';
        acc[reason] = (acc[reason] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

  return {
    summary: {
      totalCases: summary.totalCases,
      averageConfidence: Math.round(summary.averageConfidence * 100) / 100,
      visibleCases: summary.visibleCases,
      flaggedCases: summary.flaggedCases,
      flaggingRate: Math.round(summary.flaggingRate * 100) / 100,
    },
    breakdown: {
      byLevel: levelBreakdown,
      byWorkflow: workflowBreakdown,
      byFlagReason: flaggedBreakdown,
    },
    recommendations: generateRecommendations(summary, levelBreakdown),
    timestamp: new Date().toISOString(),
  };
};

// Helper function to generate recommendations based on confidence patterns
const generateRecommendations = (
  summary: any,
  levelBreakdown: Record<string, number>
) => {
  const recommendations: string[] = [];

  // High flagging rate
  if (summary.flaggingRate > 15) {
    recommendations.push(
      'High flagging rate detected. Consider reviewing document quality or adjusting confidence thresholds.'
    );
  }

  // Low average confidence
  if (summary.averageConfidence < 80) {
    recommendations.push(
      'Low average confidence. Consider improving document processing or training data.'
    );
  }

  // Too many critical cases
  if (levelBreakdown.critical > summary.totalCases * 0.05) {
    recommendations.push(
      'High number of critical cases. Review document quality and processing pipeline.'
    );
  }

  // Good performance indicators
  if (summary.flaggingRate < 5 && summary.averageConfidence > 90) {
    recommendations.push(
      'Excellent confidence performance. System is operating optimally.'
    );
  }

  return recommendations;
};

// Export all utilities (Task #16: Enhanced with error handling)
export {
  evaluateConfidence,
  generateCaseFlagging,
  calculateConfidenceMetrics,
  routeCase,
  generateRoutingSummary,
  // New error handling exports
  evaluateCaseWithErrorHandling,
  retryWithBackoff,
  flagCaseWithErrorHandling,
  createErrorState,
};
